import React, { useState, useRef, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import './GlassSelect.css';

interface Option {
  value: string;
  label: string;
}

export interface GlassSelectRef {
  focus: () => void;
  blur: () => void;
}

interface GlassSelectProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  searchable?: boolean;
  allowCustomValue?: boolean;
  onSearch?: (searchTerm: string) => void;
  onCustomValue?: (customValue: string) => void;
  filterOptions?: boolean;
  minSearchLength?: number;
}

const GlassSelect = forwardRef<GlassSelectRef, GlassSelectProps>(({
  options,
  value,
  onChange,
  placeholder = "Оберіть опцію",
  disabled = false,
  className = '',
  searchable = false,
  allowCustomValue = false,
  onSearch,
  onCustomValue,
  filterOptions = true,
  minSearchLength = 0
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const ignoreBlurRef = useRef<boolean>(false);
  const optionsContainerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(option => option.value === value);

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (searchable) {
        inputRef.current?.focus();
      } else {
        selectRef.current?.focus();
      }
    },
    blur: () => {
      if (searchable) {
        inputRef.current?.blur();
      } else {
        selectRef.current?.blur();
      }
      setIsOpen(false);
    }
  }));

  useEffect(() => {
    if (searchable) {
      if (selectedOption) {
        setDisplayValue(selectedOption.label);
      } else if (allowCustomValue && value) {
        setDisplayValue(value);
      } else {
        setDisplayValue('');
      }
    }
  }, [value, selectedOption, searchable, allowCustomValue]);

  const filteredOptions = useMemo(() => {
    if (!searchable || !filterOptions || searchTerm.length < minSearchLength) {
      return options;
    }
    
    return options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm, searchable, filterOptions, minSearchLength]);

  useEffect(() => {
    // Автоматично скролимо до вибраного елемента при відкритті dropdown
    // Працює тільки коли не використовується пошук
    if (isOpen && optionsContainerRef.current && value && (!searchable || !searchTerm)) {
      const selectedElement = optionsContainerRef.current.querySelector(
        `.glass-select-option[data-value="${value}"]`
      ) as HTMLElement;
      
      if (selectedElement) {
        // Невелика затримка для того, щоб dropdown встиг відрендеритися
        setTimeout(() => {
          selectedElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }, 100);
      }
    }
  }, [isOpen, value, filteredOptions, searchable, searchTerm]);

  useEffect(() => {
    if (isOpen) {
      const currentIndex = filteredOptions.findIndex(option => option.value === value);
      setHighlightedIndex(currentIndex >= 0 ? currentIndex : -1);
    } else {
      setHighlightedIndex(-1);
    }
  }, [isOpen, filteredOptions, value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      if (selectRef.current && !selectRef.current.contains(target) &&
          dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    const updatePosition = () => {
      if (isOpen && selectRef.current) {
        const rect = selectRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 8,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
    }

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) {
        if ((event.key === 'Enter' || event.key === ' ') && !searchable) {
          event.preventDefault();
          setIsOpen(true);
        }
        return;
      }

      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          setIsOpen(false);
          if (searchable) {
            inputRef.current?.blur();
          }
          break;

        case 'ArrowDown':
          event.preventDefault();
          setHighlightedIndex(prev => {
            const maxIndex = filteredOptions.length + (allowCustomValue && searchTerm.trim() && 
              !filteredOptions.some(opt => opt.label.toLowerCase() === searchTerm.toLowerCase()) ? 1 : 0) - 1;
            const nextIndex = prev < maxIndex ? prev + 1 : 0;
            return nextIndex;
          });
          break;

        case 'ArrowUp':
          event.preventDefault();
          setHighlightedIndex(prev => {
            const maxIndex = filteredOptions.length + (allowCustomValue && searchTerm.trim() && 
              !filteredOptions.some(opt => opt.label.toLowerCase() === searchTerm.toLowerCase()) ? 1 : 0) - 1;
            const nextIndex = prev > 0 ? prev - 1 : maxIndex;
            return nextIndex;
          });
          break;

        case 'Enter':
          event.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
            const selectedOption = filteredOptions[highlightedIndex];
            handleOptionClick(selectedOption.value);
          } else if (highlightedIndex === filteredOptions.length && allowCustomValue && searchTerm.trim()) {
            handleCustomValueCreation();
          } else if (allowCustomValue && searchTerm.trim()) {
            handleCustomValueCreation();
          }
          break;

        case 'Tab':
          setIsOpen(false);
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, highlightedIndex, filteredOptions, allowCustomValue, searchTerm, searchable]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (searchable && !isOpen) {
        setDisplayValue(selectedOption ? selectedOption.label : '');
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setDisplayValue(newValue);
    setSearchTerm(newValue);
    
    if (onSearch) {
      onSearch(newValue);
    }
    
    if (!isOpen && newValue) {
      setIsOpen(true);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      if (filteredOptions.length > 0) {
        handleOptionClick(filteredOptions[0].value);
      } else if (allowCustomValue && displayValue.trim()) {
        if (onCustomValue) {
          onCustomValue(displayValue.trim());
        } else {
          onChange(displayValue.trim());
        }
        setIsOpen(false);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleInputBlur = () => {
    if (ignoreBlurRef.current) return;
    setIsOpen(false);
    setSearchTerm('');
    setDisplayValue(selectedOption ? selectedOption.label : '');
  };

  const handleOptionClick = (optionValue: string) => {
    ignoreBlurRef.current = true;
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
    
    const option = options.find(opt => opt.value === optionValue);
    if (option && searchable) {
      setDisplayValue(option.label);
    }
    
    if (searchable) {
      inputRef.current?.blur();
    }
    setTimeout(() => { ignoreBlurRef.current = false; }, 0);
  };

  const handleCustomValueCreation = () => {
    if (allowCustomValue && searchTerm.trim()) {
      if (onCustomValue) {
        onCustomValue(searchTerm.trim());
      } else {
        onChange(searchTerm.trim());
      }
      setIsOpen(false);
      setSearchTerm('');
      if (searchable) {
        setDisplayValue(searchTerm.trim());
        inputRef.current?.blur();
      }
    }
  };

  const renderDropdown = () => {
    if (!isOpen) return null;

    const optionsToShow = filteredOptions;
    const showNoResults = searchable && searchTerm.length >= minSearchLength && optionsToShow.length === 0;
    const showCreateOption = allowCustomValue && searchTerm.trim() && 
                            !optionsToShow.some(opt => opt.label.toLowerCase() === searchTerm.toLowerCase());

    return createPortal(
      <div 
        ref={dropdownRef}
        className="glass-select-dropdown"
        style={{
          position: 'absolute',
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          zIndex: 9999999
        }}
      >
        <div className="glass-select-options" ref={optionsContainerRef}>
          {optionsToShow.map((option, index) => (
            <div
              key={option.value}
              data-value={option.value}
              className={`glass-select-option ${
                option.value === value ? 'selected' : ''
              } ${
                index === highlightedIndex ? 'highlighted' : ''
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleOptionClick(option.value);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {option.label}
              {option.value === value && (
                <span className="glass-select-check">✓</span>
              )}
            </div>
          ))}
          
          {showCreateOption && (
            <div
              className={`glass-select-option glass-select-create ${
                optionsToShow.length === highlightedIndex ? 'highlighted' : ''
              }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCustomValueCreation();
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseEnter={() => setHighlightedIndex(optionsToShow.length)}
            >
              <span>Створити: "{searchTerm.trim()}"</span>
              <span className="glass-select-create-icon">+</span>
            </div>
          )}
          
          {showNoResults && (
            <div className="glass-select-option glass-select-no-results">
              Нічого не знайдено
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div 
      className={`glass-select ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''} ${searchable ? 'searchable' : ''} ${className}`} 
      ref={selectRef}
      tabIndex={disabled ? -1 : 0}
      onFocus={() => {
        if (!searchable && !disabled) {
          selectRef.current?.focus();
        }
      }}
    >
      <div 
        className={`glass-select-trigger ${isOpen ? 'open' : ''}`}
        onClick={searchable ? undefined : handleToggle}
      >
        {searchable ? (
          <input
            ref={inputRef}
            type="text"
            className="glass-select-input"
            value={isOpen ? displayValue : (selectedOption ? selectedOption.label : '')}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onBlur={handleInputBlur}
            onClick={() => setIsOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
          />
        ) : (
          <span className="glass-select-value">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        )}
        
        <span 
          className="glass-select-arrow"
          onClick={searchable ? handleToggle : undefined}
          style={searchable ? { cursor: 'pointer' } : undefined}
        >
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
            <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </div>
      
      {renderDropdown()}
    </div>
  );
});

GlassSelect.displayName = 'GlassSelect';

export default GlassSelect;
